<template>
  <div class="sc-code-editor" :style="{ height: _height }">
    <textarea ref="textarea" v-model="displayValue"></textarea>
    <!-- 格式化结果提示 -->
    <div v-if="formatMessage" class="format-toast">{{ formatMessage }}</div>
  </div>
</template>

<script>
import { markRaw } from "vue"
import CodeMirror from "codemirror"
import "codemirror/lib/codemirror.css"
import "codemirror/theme/idea.css"
import "codemirror/theme/darcula.css"
import "codemirror/addon/selection/active-line"
import "codemirror/addon/edit/matchbrackets"
import "codemirror/addon/edit/closebrackets"
import "codemirror/mode/javascript/javascript"
import "codemirror/mode/sql/sql"
import { js_beautify } from "js-beautify"

export default {
  props: {
    modelValue: { type: [String, Object, Array], default: "" },
    mode: { type: String, default: "javascript" },
    height: { type: [String, Number], default: 300 },
    options: { type: Object, default: () => ({}) },
    theme: { type: String, default: "idea" },
    readOnly: { type: Boolean, default: false },
    autoFormatOnPaste: { type: Boolean, default: false },
    autoFormatOnBlur: { type: Boolean, default: false },
    autoRepair: { type: Boolean, default: true },
    emitObject: { type: Boolean, default: false }
  },
  data() {
    return {
      displayValue: "",
      coder: null,
      formatMessage: "", // 提示消息
      formatTimer: null, // 定时器
      opt: {
        theme: this.theme,
        styleActiveLine: true,
        lineNumbers: true,
        lineWrapping: false,
        tabSize: 2,
        indentUnit: 2,
        indentWithTabs: false,
        mode: this.mode,
        readOnly: this.readOnly,
        matchBrackets: true,
        autoCloseBrackets: true,
        extraKeys: {
          "Shift-Alt-F": this.manualFormat.bind(this),
          "Ctrl-S": () => { this.manualFormat(); return true }
        },
        ...this.options
      }
    }
  },
  computed: {
    _height() {
      return Number(this.height) ? Number(this.height) + "px" : this.height
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        const newStr = typeof newVal === 'string' ? newVal : this.toJsonString(newVal)
        if (this.coder && newStr !== this.coder.getValue()) {
          this.coder.setValue(newStr)
          this.displayValue = newStr
        } else if (!this.coder) {
          this.displayValue = newStr
        }
      },
      deep: true
    }
  },
  mounted() {
    this.init()
  },
  beforeUnmount() {
    if (this.formatTimer) clearTimeout(this.formatTimer)
  },
  methods: {
    // 获取当前模式对应的语言名称
    getLanguageName() {
      const mode = this.mode || this.opt.mode
      if (mode === "javascript" || mode === "js") return "JavaScript"
      if (mode === "json") return "JSON"
      if (mode === "sql") return "SQL"
      return "代码"
    },

    // 显示提示消息
    showMessage(text) {
      this.formatMessage = text
      if (this.formatTimer) clearTimeout(this.formatTimer)
      this.formatTimer = setTimeout(() => {
        this.formatMessage = ""
      }, 2000)
    },

    toJsonString(value) {
      if (typeof value === "string") {
        if (value.trim() === "") return ""
        try {
          return JSON.stringify(JSON.parse(value), null, 2)
        } catch (e) {
          if (this.autoRepair) {
            const repaired = this.repairJson(value)
            try {
              return JSON.stringify(JSON.parse(repaired), null, 2)
            } catch (e2) {
              return value
            }
          }
          return value
        }
      } else if (typeof value === "object" && value !== null) {
        try {
          return JSON.stringify(value, null, 2)
        } catch (e) {
          return ""
        }
      }
      return ""
    },

    repairJson(str) {
      if (typeof str !== "string") return str
      str = str.trim()
      return str.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
    },

    tryFormatCode() {
      const currentValue = this.coder.getValue()
      if (!currentValue || currentValue.trim() === "") {
        this.showMessage(`${this.getLanguageName()} 内容为空，无需格式化`)
        return false
      }

      const mode = this.mode || this.opt.mode
      let formatted = null
      let success = false

      if (mode === "javascript" || mode === "js") {
        try {
          formatted = js_beautify(currentValue, {
            indent_size: this.opt.indentUnit || 2,
            indent_with_tabs: this.opt.indentWithTabs || false,
            max_preserve_newlines: 2,
            preserve_newlines: true
          })
          success = true
        } catch (e) {
          console.warn("JavaScript format failed:", e)
          this.showMessage(`${this.getLanguageName()} 格式化失败`)
          return false
        }
      } else {
        // JSON 模式
        let parsed = null
        let finalValue = currentValue

        try {
          parsed = JSON.parse(currentValue)
          success = true
        } catch (e) {
          if (this.autoRepair) {
            const repaired = this.repairJson(currentValue)
            try {
              parsed = JSON.parse(repaired)
              finalValue = repaired
              success = true
            } catch (e2) {
              this.showMessage(`${this.getLanguageName()} 格式不正确，无法格式化`)
              return false
            }
          } else {
            this.showMessage(`${this.getLanguageName()} 格式不正确，无法格式化`)
            return false
          }
        }
        if (success) {
          formatted = JSON.stringify(parsed, null, 2)
          if (formatted === finalValue) {
            // 内容已经格式化
            this.showMessage(`${this.getLanguageName()} 已是最佳格式`)
            return false
          }
        }
      }

      if (!success || formatted === currentValue) {
        this.showMessage(`${this.getLanguageName()} 无需格式化`)
        return false
      }

      // 替换内容
      this.coder.setValue(formatted)
      this.displayValue = formatted
      this.emitChange(formatted)
      this.showMessage(`${this.getLanguageName()} 格式化成功`)
      return true
    },

    emitChange(strValue) {
      if (this.emitObject) {
        try {
          const obj = JSON.parse(strValue)
          this.$emit("update:modelValue", obj)
        } catch (e) {
          this.$emit("update:modelValue", strValue)
        }
      } else {
        this.$emit("update:modelValue", strValue)
      }
    },

    manualFormat() {
      return this.tryFormatCode()
    },

    format() {
      return this.manualFormat()
    },

    getCodeMirror() {
      return this.coder
    },

    init() {
      const initial = typeof this.modelValue === 'string'
        ? this.modelValue
        : this.toJsonString(this.modelValue)
      this.displayValue = initial

      this.coder = markRaw(CodeMirror.fromTextArea(this.$refs.textarea, this.opt))
      this.coder.setValue(initial)

      this.coder.on("change", (coder) => {
        const newValue = coder.getValue()
        this.displayValue = newValue
        this.emitChange(newValue)
      })
    }
  }
}
</script>

<style scoped>
.sc-code-editor {
  font-size: 14px;
  border: 1px solid #ddd;
  line-height: 150%;
  position: relative;
}

.sc-code-editor:deep(.CodeMirror) {
  height: 100%;
}

.sc-code-editor:deep(.CodeMirror-focused) {
  border-color: #409eff;
}

.sc-code-editor:hover::after {
  content: "Shift+Alt+F / Ctrl+S 格式化";
  position: absolute;
  bottom: 5px;
  right: 10px;
  font-size: 12px;
  color: #999;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10;
}

/* 格式化提示条 */
.format-toast {
  position: absolute;
  top: 8px;
  right: 12px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 13px;
  z-index: 20;
  pointer-events: none;
  animation: fadeInOut 2s ease;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }

  10% {
    opacity: 1;
    transform: translateY(0);
  }

  80% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}
</style>